/**
 * TowerMiniIcon.tsx -- compact DNA-health strip for small/list contexts
 *
 * W20 (2026-05-28): Replaces the SVG tower at small scale.
 * Per Bryan K. Short direction: "I would rather have a basic indicator
 * with maybe the DNA health of each opportunity and just an icon."
 *
 * Renders a 10-cell horizontal strip (one cell per CP) colored by state,
 * plus a CP{n} glyph, plus open-account count. No tower image.
 *
 * Canon 5-state palette (CANON_VISUALS_LOCKED.md, locked 2026-05-27):
 *   not-started -> gray   #6B7280
 *   failed      -> red    #DC2626
 *   at-risk     -> orange #EA580C
 *   in-progress -> yellow #EAB308
 *   passed      -> green  #16A34A
 *
 * Brand: No exclamation points. No emojis. M-PACT hyphenated.
 */

import { HEALTH_PALETTE, getHealthColor } from "@shared/schema";

export interface TowerMiniIconProps {
  /** Composite score 0-100 -- fallback tint when cpScores absent */
  composite: number;
  /** Per-CP scores (0-4, 10 entries, index 0 = CP1). Optional. */
  cpScores?: number[] | null;
  /** Current CP number 1-10. When provided, renders CP{n} glyph. */
  currentCp?: number | null;
  /** Open account count displayed on the right of the strip. */
  openCount?: number | null;
  /** Kept for backward-compat; not used in new layout. */
  width?: number;
  height?: number;
  ariaLabel?: string;
}

const CP_COUNT = 10;

function cpScoreToColor(score: number | null | undefined): string {
  if (score == null) return HEALTH_PALETTE.gray;
  if (score >= 4)    return HEALTH_PALETTE.green;
  if (score >= 3)    return HEALTH_PALETTE.yellow;
  if (score >= 2)    return HEALTH_PALETTE.orange;
  if (score >= 1)    return HEALTH_PALETTE.red;
  return HEALTH_PALETTE.gray;
}

export default function TowerMiniIcon({
  composite,
  cpScores = null,
  currentCp = null,
  openCount = null,
  width: _w,
  height: _h,
  ariaLabel,
}: TowerMiniIconProps) {
  const validCp = currentCp != null && currentCp >= 1 && currentCp <= 10
    ? currentCp : null;

  const fallback = getHealthColor(composite > 0 ? composite : null);

  const cells: string[] = Array.from({ length: CP_COUNT }, (_, idx) => {
    if (cpScores && cpScores.length > idx) return cpScoreToColor(cpScores[idx]);
    if (composite <= 0) return HEALTH_PALETTE.gray;
    return fallback;
  });

  return (
    <div
      role="img"
      aria-label={ariaLabel ?? `DNA health, score ${composite}`}
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 3, flexShrink: 0 }}
    >
      {/* 10-cell strip */}
      <div style={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
        {cells.map((color, idx) => {
          const cpNum = idx + 1;
          const isCurrent = validCp === cpNum;
          return (
            <div
              key={cpNum}
              title={`CP${cpNum}`}
              style={{
                width:  isCurrent ? 10 : 8,
                height: isCurrent ? 10 : 8,
                borderRadius: 2,
                background: color,
                flexShrink: 0,
                boxShadow: isCurrent
                  ? `0 0 0 1.5px rgba(255,255,255,0.85), 0 0 5px 1px ${color}99`
                  : "none",
              }}
            />
          );
        })}
      </div>

      {/* CP glyph + open count */}
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 4 }}>
        <span style={{
          fontFamily: "Inter, system-ui, sans-serif",
          fontSize: 9, fontWeight: validCp != null ? 700 : 600,
          color: validCp != null ? (cells[validCp - 1] ?? HEALTH_PALETTE.gray) : "#9CA3AF",
          letterSpacing: "0.04em", lineHeight: 1, whiteSpace: "nowrap",
        }}>
          {validCp != null ? `CP${validCp}` : "-"}
        </span>
        {openCount != null && openCount > 0 && (
          <span style={{
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: 9, fontWeight: 600,
            color: "#6B7280",
            letterSpacing: "0.02em", lineHeight: 1, whiteSpace: "nowrap",
          }}>
            {openCount}
          </span>
        )}
      </div>
    </div>
  );
}
